import { Component } from '@theme/component';

/** @typedef {{ slideshowContainer: HTMLElement, slides?: HTMLElement[], previous?: HTMLButtonElement, next?: HTMLButtonElement, autoplayToggle?: HTMLButtonElement, autoplayToggleLabel?: HTMLElement }} Refs */
/** @extends {Component<Refs>} */
export class AnnouncementBar extends Component {
  #current = 0;
  #interval;
  #connected = false;
  #hovered = false;
  #focused = false;
  #explicitlyPaused = false;
  #motionPaused = false;
  #motionQuery;

  connectedCallback() {
    super.connectedCallback();
    if (this.#connected) return;
    this.#connected = true;
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motionPaused = this.#motionQuery.matches;
    this.addEventListener('mouseenter', this.#handleMouseEnter);
    this.addEventListener('mouseleave', this.#handleMouseLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#motionQuery.addEventListener('change', this.#handleMotionChange);
    this.current = this.#current;
    this.#sync();
  }

  disconnectedCallback() {
    this.#connected = false;
    this.suspend();
    this.removeEventListener('mouseenter', this.#handleMouseEnter);
    this.removeEventListener('mouseleave', this.#handleMouseLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#motionQuery?.removeEventListener('change', this.#handleMotionChange);
    super.disconnectedCallback?.();
  }

  next() { this.current += 1; }
  previous() { this.current -= 1; }

  play() {
    this.#explicitlyPaused = false;
    if (this.#motionQuery?.matches) this.#motionPaused = false;
    this.#sync();
  }

  pause() {
    this.#explicitlyPaused = true;
    this.#sync();
  }

  togglePlayback() {
    if (this.#explicitlyPaused || this.#motionPaused) this.play();
    else this.pause();
  }

  suspend() {
    if (this.#interval !== undefined) clearInterval(this.#interval);
    this.#interval = undefined;
  }

  resume() { this.#sync(); }

  get paused() { return this.hasAttribute('paused'); }
  set paused(value) { this.toggleAttribute('paused', value); }

  get autoplay() { return Boolean(this.autoplayInterval); }
  get autoplayInterval() {
    const value = Number.parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isFinite(value) && value > 0 ? value * 1000 : undefined;
  }

  get current() { return this.#current; }
  set current(value) {
    this.#current = value;
    const slides = this.refs.slides ?? [];
    if (slides.length === 0) return;
    let relativeIndex = value % slides.length;
    if (relativeIndex < 0) relativeIndex += slides.length;
    slides.forEach((slide, index) => slide.setAttribute('aria-hidden', String(index !== relativeIndex)));
  }

  #sync() {
    const persistentlyPaused = this.#explicitlyPaused || this.#motionPaused;
    this.paused = persistentlyPaused;
    const action = persistentlyPaused ? 'Play' : 'Pause';
    this.refs.autoplayToggle?.setAttribute('aria-label', `${action} announcements`);
    if (this.refs.autoplayToggleLabel) this.refs.autoplayToggleLabel.textContent = action;
    this.refs.slideshowContainer?.setAttribute('aria-live', persistentlyPaused || this.#focused ? 'polite' : 'off');

    const canPlay = this.#connected && this.autoplay && (this.refs.slides?.length ?? 0) > 1 && !document.hidden && !this.#hovered && !this.#focused && !persistentlyPaused;
    if (!canPlay) {
      this.suspend();
      return;
    }
    if (this.#interval === undefined) {
      this.#interval = setInterval(() => this.next(), this.autoplayInterval);
    }
  }

  #handleMouseEnter = () => { this.#hovered = true; this.#sync(); };
  #handleMouseLeave = () => { this.#hovered = false; this.#sync(); };
  #handleFocusIn = () => { this.#focused = true; this.#sync(); };
  #handleFocusOut = (event) => {
    if (this.contains(event.relatedTarget)) return;
    this.#focused = false;
    this.#sync();
  };
  #handleVisibilityChange = () => this.#sync();
  #handleMotionChange = (event) => { this.#motionPaused = event.matches; this.#sync(); };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
